import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router-dom';

import { updateCustomer } from 'api/customers';
import { getAllTechnicians } from 'api/technicians';
import { getAllDoctors } from 'api/doctors';
import { getPolygraphyById, updatePolygraphy } from 'api/polygraphies';

import { UserContext } from 'contexts/user';
import { sanitizePolygraphy } from 'helpers/general';

import {
  NEXT_ACTIONS,
  MANUAL_PAYMENT_STATUSES,
  RECORDING_STATUSES,
  STATUSES,
} from 'config/polygraphy';
import { BLANK } from 'config/shared';

import ViewComponent from './view_component';
import MobileViewComponent from './view_component/mobile';

import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';

const PolygraphiesEdit = ({ match, history, onCountUpdate, onMessages }) => {
  const { user } = useContext(UserContext);
  const [rawPolygraphy, setRawPolygraphy] = useState({});
  const [polygraphy, setPolygraphy] = useState({});
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [validation, setValidation] = useState({
    customer: true,
    customerPhone: true,
    customerEmail: true,
    customerCity: true,
    customerAddress: true,
    technician: true,
    doctor: true,
    deliveryDate: true,
    pickupDate: true,
    polygraphyStatus: true,
    recordingStatus: true,
    nextAction: true,
    manualPaymentStatus: true,
  });
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [originalCustomer, setOriginalCustomer] = useState({});
  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);

  // fetch data
  useEffect(() => {
    async function fetchData() {
      setRawPolygraphy(await getPolygraphyById(match.params.id));
      setAllTechnicians(await getAllTechnicians());
      setAllDoctors(await getAllDoctors());
    }
    fetchData();
  }, [match.params.id]);

  // set technician options
  useEffect(() => {
    const newTechnicianOptions = allTechnicians.map((d) => ({
      id: d.id,
      value: d.id,
      label: `${d.firstName} ${d.lastName}`,
      firstName: d.firstName,
      lastName: d.lastName,
    }));

    setTechnicianOptions([BLANK, ...newTechnicianOptions]);
  }, [allTechnicians]);

  // set doctor options
  useEffect(() => {
    const newDoctorOptions = allDoctors
      .map((d) => ({
        id: d.id,
        value: d.id,
        label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        firstName: d.firstName,
        lastName: d.lastName,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label));

    setDoctorOptions([BLANK, ...newDoctorOptions]);
  }, [allDoctors]);

  // set local polygraphy object
  useEffect(() => {
    setPolygraphy({
      ...rawPolygraphy,
      customer: rawPolygraphy.customer && {
        id: rawPolygraphy.customer.id,
        value: `${rawPolygraphy.customer.firstName} ${rawPolygraphy.customer.lastName}`,
        label: `${rawPolygraphy.customer.firstName} ${rawPolygraphy.customer.lastName}`,
        phone: rawPolygraphy.customer.phone,
        email: rawPolygraphy.customer.email,
        city: rawPolygraphy.customer.city,
        address: rawPolygraphy.customer.address,
      },
      status: STATUSES.find((s) => s.value === rawPolygraphy.status),
      recordingStatus: RECORDING_STATUSES.find(
        (s) => s.value === rawPolygraphy.recordingStatus,
      ),
      nextAction: NEXT_ACTIONS.find(
        (s) => s.value === rawPolygraphy.nextAction,
      ),
      manualPaymentStatus: MANUAL_PAYMENT_STATUSES.find(
        (s) => s.value === rawPolygraphy.manualPaymentStatus,
      ),
      technician: rawPolygraphy.technician
        ? technicianOptions.find((d) => d.id === rawPolygraphy.technician.id)
        : BLANK,
      doctor: rawPolygraphy.doctor
        ? doctorOptions.find((d) => d.id === rawPolygraphy.doctor.id)
        : BLANK,
    });
  }, [rawPolygraphy, technicianOptions, doctorOptions]);

  // save origin customer
  useEffect(() => {
    setOriginalCustomer({ ...rawPolygraphy.customer });
  }, [rawPolygraphy]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setPolygraphy({ ...polygraphy, [name]: value });
  };

  const handleCustomerChange = (e) => {
    const { customer } = polygraphy;
    const { name, value } = e.target;

    setPolygraphy({ ...polygraphy, customer: { ...customer, [name]: value } });
  };

  const handleTechnicianSelect = (selectedOption) => {
    setPolygraphy({ ...polygraphy, technician: selectedOption });
    setValidation({ ...validation, technician: !!selectedOption });
  };

  const handleDoctorSelect = (selectedOption) => {
    setPolygraphy({ ...polygraphy, doctor: selectedOption });
    setValidation({ ...validation, doctor: !!selectedOption });
  };

  const handleDateChange = ({ field, value }) =>
    setPolygraphy({ ...polygraphy, [field]: value });
  const handleSelectChange = (field, selectedOption) =>
    handleDateChange({ field, value: selectedOption });

  const checkValidity = () => {
    const { customer, doctor } = polygraphy;

    const validation = {
      customer: !!customer.id,
      customerPhone: true,
      customerEmail: true,
      customerCity: true,
      customerAddress: true,
      technician: true,
      doctor: !!doctor.id,
      deliveryDate: true,
      pickupDate: true,
      polygraphyStatus: true,
      recordingStatus: true,
      nextAction: true,
      manualPaymentStatus: true,
    };

    setValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  const handleSubmit = async () => {
    const isValid = checkValidity();

    if (isValid) {
      const newPolygraphy = {
        ...polygraphy,
        status: polygraphy.status?.value,
        recordingStatus: polygraphy.recordingStatus?.value,
        nextAction: polygraphy.nextAction?.value,
        manualPaymentStatus: polygraphy.manualPaymentStatus?.value,
        customer: polygraphy.customer?.id,
      };

      const sanitizedPolygraphy = sanitizePolygraphy(newPolygraphy);

      const {
        meta: { messages },
      } = await updatePolygraphy(match.params.id, sanitizedPolygraphy, {
        showMessage: false,
      });

      // update customer only if it has changed
      if (
        JSON.stringify({
          phone: polygraphy.customer.phone,
          email: polygraphy.customer.email,
          city: polygraphy.customer.city,
          address: polygraphy.customer.address,
        }) !==
        JSON.stringify({
          phone: originalCustomer.phone,
          email: originalCustomer.email,
          city: originalCustomer.city,
          address: originalCustomer.address,
        })
      ) {
        await updateCustomer(polygraphy.customer.id, {
          phone: polygraphy.customer.phone,
          email: polygraphy.customer.email,
          city: polygraphy.customer.city,
          address: polygraphy.customer.address,
        });
      }

      onCountUpdate();
      onMessages(messages);

      history.goBack();
    }
  };

  const props = {
    onChange: handleChange,
    onCustomerChange: handleCustomerChange,
    onDateChange: handleDateChange,
    onSelectChange: handleSelectChange,
    onSubmit: handleSubmit,
    onTechnicianSelect: handleTechnicianSelect,
    onDoctorSelect: handleDoctorSelect,
    polygraphy,
    technicianOptions,
    doctorOptions,
    validation,
    user,
  };

  return isMobile ? (
    <MobileViewComponent {...props} />
  ) : (
    <ViewComponent {...props} />
  );
};

PolygraphiesEdit.propTypes = {
  match: ReactRouterPropTypes.match.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default withRouter(PolygraphiesEdit);
